import { useState, useEffect } from "react";
import client from "../api/client";
import { useNavigate } from "react-router-dom";
import "./Login.css";
import { FaBolt as FaBoltIcon, FaArrowRight } from "react-icons/fa";
import {
  AUTH_TOKEN_KEY,
  AUTH_USER_KEY,
  QUICK_SESSION_TOKEN_KEY,
  clearAccessAuth,
  clearStoredAuth,
  getHomeRouteForRole,
  isPinAuthenticated,
  isTokenExpired,
  markPinAuthenticated,
} from "../utils/authSession";
import AboutSection from "../components/AboutSection";
import FloatingNavbar from "../components/FloatingNavbar";
import { useMasterPins } from "../context/PinContext";
import { getEmployeeIdForProtectedAction, getStoredUser } from "../utils/masterPinActions";
import { logEmployeeActivity } from "../utils/activityLogger";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [pin, setPin] = useState("");
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [loginMode, setLoginMode] = useState("password");
  const [quickSessionToken, setQuickSessionToken] = useState(
    localStorage.getItem(QUICK_SESSION_TOKEN_KEY) || ""
  );
  const [rememberMe, setRememberMe] = useState(false);
  const navigate = useNavigate();
  const { verifyPinOverride } = useMasterPins();

  const navigateByRole = (role) => {
    navigate(getHomeRouteForRole(role), { replace: true });
  };

  const reloadByRole = (role) => {
    setTimeout(() => {
      window.location.href = getHomeRouteForRole(role);
    }, 50);
  };

  const openLoginModal = () => {
    setError("");
    setPin("");
    setLoginMode(quickSessionToken ? "pin" : "password");
    setShowLoginModal(true);
  };

  // Clear error when user starts typing
  const clearError = () => {
    if (error) setError("");
  };

  useEffect(() => {
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    if (token && isTokenExpired(token)) {
      clearAccessAuth();
    }

    const activeToken = localStorage.getItem(AUTH_TOKEN_KEY);
    if (activeToken && isPinAuthenticated()) {
      const storedUser = JSON.parse(localStorage.getItem(AUTH_USER_KEY) || "{}");
      navigateByRole(storedUser?.role);
      return;
    }

    const storedQuickSessionToken =
      localStorage.getItem(QUICK_SESSION_TOKEN_KEY) || activeToken;
    if (!storedQuickSessionToken) {
      return;
    }

    const hydrateQuickSession = async () => {
      try {
        const res = await client.post("/auth/quick-session", {
          quickSessionToken: storedQuickSessionToken,
        });

        if (!res.data?.valid) {
          throw new Error("Invalid session");
        }

        if (res.data?.user) {
          localStorage.setItem(AUTH_USER_KEY, JSON.stringify(res.data.user));
        }

        if (res.data?.hasLoginPin) {
          setQuickSessionToken(storedQuickSessionToken);
          setLoginMode("pin");
          setShowLoginModal(true);
        } else {
          localStorage.removeItem(QUICK_SESSION_TOKEN_KEY);
          setQuickSessionToken("");
        }
      } catch (err) {
        const status = err?.response?.status;
        if (status === 404 || status === 503) {
          // Backend does not support quick PIN APIs in this deployment.
          localStorage.removeItem(QUICK_SESSION_TOKEN_KEY);
          setQuickSessionToken("");
          setLoginMode("password");
          setError("Quick PIN login is unavailable right now. Please login with email and password.");
          setShowLoginModal(true);
          return;
        }
        clearStoredAuth();
        setQuickSessionToken("");
        setLoginMode("password");
      }
    };

    hydrateQuickSession();
  }, [navigate]);

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!email.trim() || !password.trim()) {
      setError("Please enter both email and password.");
      return;
    }
    
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError("Please enter a valid email address.");
      return;
    }
    
    setIsLoading(true);
    setError("");

    try {
      const res = await client.post("/auth/login", { email, password });
      localStorage.setItem(AUTH_TOKEN_KEY, res.data.token);
      localStorage.setItem(AUTH_USER_KEY, JSON.stringify(res.data.user));

      const sessionToken = res.data.quickSessionToken || res.data.token;
      if (sessionToken) {
        localStorage.setItem(QUICK_SESSION_TOKEN_KEY, sessionToken);
        setQuickSessionToken(sessionToken);
      } else {
        localStorage.removeItem(QUICK_SESSION_TOKEN_KEY);
        setQuickSessionToken("");
      }

      markPinAuthenticated();
      reloadByRole(res.data.user?.role);

    } catch (err) {
      let errorMessage = "Login failed. Try again.";
      if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      } else if (err.response?.status === 400) {
        errorMessage = "User not found. Please check your email.";
      } else if (err.response?.status === 401 || err.response?.status === 403) {
        errorMessage = "Incorrect password. Please try again.";
      } else if (err.response?.status === 500) {
        errorMessage = "Server error. Please try again later.";
      } else if (err.message) {
        errorMessage = err.message;
      } else if (err.code === "NETWORK_ERROR") {
        errorMessage = "Network error. Please check your connection.";
      } else if (err.code === "ECONNREFUSED") {
        errorMessage = "Cannot connect to server. Please check your internet connection.";
      } else if (err.message?.includes("CORS")) {
        errorMessage = "CORS error. Please check server configuration.";
      } else if (err.message?.includes("Network Error")) {
        errorMessage = "Network error. Please check your internet connection or try again.";
      }
      
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePinSubmit = async (e) => {
    e.preventDefault();

    if (!/^\d{4}$/.test(pin.trim())) {
      setError("Please enter a valid 4-digit PIN.");
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      const storedUser = getStoredUser();
      const employeeId = getEmployeeIdForProtectedAction(storedUser);
      const isOverrideValid =
        employeeId && (await verifyPinOverride(employeeId, "login", pin.trim()));

      if (isOverrideValid) {
        const activeAccessToken = localStorage.getItem(AUTH_TOKEN_KEY) || quickSessionToken;
        if (activeAccessToken) {
          localStorage.setItem(AUTH_TOKEN_KEY, activeAccessToken);
        }
        if (storedUser && Object.keys(storedUser).length > 0) {
          localStorage.setItem(AUTH_USER_KEY, JSON.stringify(storedUser));
        }

        if (!activeAccessToken || !storedUser?.role) {
          setError("No active session found. Please login with email and password.");
          setLoginMode("password");
          return;
        }

        void logEmployeeActivity({
          targetEmployeeId: storedUser?.id || null,
          targetEmployeeName: storedUser?.name || "Unknown User",
          action: "Master PIN Login",
          module: "Auth",
          description: `Unlocked dashboard with master PIN override as ${storedUser?.name || "Unknown User"}.`,
        });

        markPinAuthenticated();
        setPin("");
        reloadByRole(storedUser?.role);
        return;
      }

      if (!quickSessionToken) {
        setError("No active quick session found. Please login with email and password.");
        setLoginMode("password");
        return;
      }

      const res = await client.post("/auth/verify-pin", {
        pin: pin.trim(),
        quickSessionToken,
      });

      localStorage.setItem(AUTH_TOKEN_KEY, res.data.token);
      localStorage.setItem(AUTH_USER_KEY, JSON.stringify(res.data.user));
      if (res.data.quickSessionToken) {
        localStorage.setItem(QUICK_SESSION_TOKEN_KEY, res.data.quickSessionToken);
        setQuickSessionToken(res.data.quickSessionToken);
      }

      markPinAuthenticated();
      setPin("");
      reloadByRole(res.data.user?.role);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401) {
        setError("Invalid PIN");
      } else if (status === 404 || status === 503) {
        localStorage.removeItem(QUICK_SESSION_TOKEN_KEY);
        setQuickSessionToken("");
        setPin("");
        setLoginMode("password");
        setError("PIN login is unavailable right now. Please login with email and password.");
      } else if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else {
        setError("PIN verification failed. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="saas-landing">
      <FloatingNavbar />
      {/* HERO SECTION */}
      <section id="home" className="hero-section">
        <video
          className="hero-bg-video"
          autoPlay
          loop
          muted
          playsInline
        >
          <source src="https://techiohisab.com/video.mp4" type="video/mp4" />
        </video>

        <div className="hero-overlay"></div>
        <div className="hero-container">
          <div className="hero-left">
            <h1 className="hero-title">
              Smart E-Commerce Business Control, <span className="highlight">Techiohisab</span>
            </h1>
            <p className="hero-subtitle">
              Manage orders, track inventory, and scale your business with your all-in-one professional admin dashboard.
            </p>
            <button className="btn-primary login-btn" onClick={openLoginModal}>
              Login to Dashboard <FaArrowRight className="btn-icon" />
            </button>
          </div>
        </div>
      </section>

      <AboutSection />

      {showLoginModal && (
        <div className="login-page-overlay">
          <video
            className="login-bg-video"
            src="https://techiohisab.com/cdu.mp4"
            autoPlay
            loop
            muted
            playsInline
          />
          <div className="login-page-inner">
            <div className="login-page-column info-column">
              <button
                className="close-modal-btn"
                onClick={() => setShowLoginModal(false)}
                aria-label="Close login screen"
              >
                ×
              </button>
              <div className="info-badge">
                <FaBoltIcon />
              </div>
              <h3>Hey, Hello!</h3>
              <p className="info-subtitle">Join the waitlist for the smartest e-commerce dashboard.</p>
              <ul className="info-list">
                <li>Unified orders, inventory, and analytics.</li>
                <li>Realtime insights for modern sellers.</li>
                <li>Secure access with adaptive authentication.</li>
                <li>Dedicated support & automation tools.</li>
              </ul>
            </div>
            <div className="login-page-column form-column">
              <div className="login-card">
                <h2>Welcome Back!</h2>
                <p>Please enter your credentials to access your account.</p>
                {error && <div className="error-message">{error}</div>}
                {loginMode === "pin" ? (
                  <form onSubmit={handlePinSubmit} className="login-form">
                    <div className="form-group">
                      <label>Security PIN</label>
                      <div className="input-wrapper">
                        <input
                          type="password"
                          inputMode="numeric"
                          maxLength={4}
                          pattern="[0-9]*"
                          placeholder="Enter 4-digit PIN"
                          value={pin}
                          onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, "").slice(0, 4);
                            setPin(value);
                            clearError();
                          }}
                          required
                          className="form-input pin-input"
                        />
                      </div>
                    </div>
                    <button type="submit" className="login-submit-btn" disabled={isLoading}>
                      {isLoading ? <span className="loading-spinner"></span> : "Unlock Dashboard"}
                    </button>
                    <button
                      type="button"
                      className="switch-login-mode-btn"
                      onClick={() => {
                        setLoginMode("password");
                        setError("");
                      }}
                    >
                      Use Email + Password
                    </button>
                  </form>
                ) : (
                  <form onSubmit={handlePasswordSubmit} className="login-form">
                    <div className="form-group">
                      <label>Email Address</label>
                        <div className="input-wrapper">
                          <input
                            type="email"
                            placeholder="Enter your email"
                            value={email}
                          onChange={(e) => {
                            setEmail(e.target.value);
                            clearError();
                          }}
                          required
                          className="form-input"
                        />
                      </div>
                    </div>
                    <div className="form-group">
                      <label>Password</label>
                        <div className="input-wrapper">
                          <input
                            type={showPassword ? "text" : "password"}
                            placeholder="Enter your password"
                          value={password}
                          onChange={(e) => {
                            setPassword(e.target.value);
                            clearError();
                          }}
                          required
                          className="form-input"
                        />
                        <button
                          type="button"
                          className="password-toggle"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? "Hide" : "Show"}
                        </button>
                      </div>
                    </div>
                    <div className="form-options">
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={rememberMe}
                          onChange={(e) => setRememberMe(e.target.checked)}
                        />
                        <span className="checkmark"></span>
                        Remember me
                      </label>
                    </div>
                    <button type="submit" className="login-submit-btn" disabled={isLoading}>
                      {isLoading ? <span className="loading-spinner"></span> : "Sign In"}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
